import { Avatar, AvatarFallback, AvatarImage, Button } from '@quadminds/mindkit';
import { TextField } from '@quadminds/ui-kit';
import { Trash2 } from 'lucide-react';
import TooltipFilePermission from './TooltipFilePermission';
import usePermissions from '../../../../../../hooks/usePermissions';
import { DocumentRoles } from '../../../../../../interfaces/document/document.input';
import { FileItem } from '../../../../../../types/File/File.types';
import { isAdminRoleName } from '../../../../../../utils/utils';

interface FilePermissionPanelProps {
    readonly file: FileItem;
    readonly onClose: () => void;
    readonly ownerAvatar?: string;
    readonly isOpen: boolean;
    readonly isVisible: boolean;
}

export function FilePermissionPanel({ file, onClose, ownerAvatar, isOpen, isVisible }: FilePermissionPanelProps) {
    const {
        searchValue,
        suggestions,
        selectedUsers,
        showSuggestions,
        suggestionsRef,
        inputRef,
        getAllPermissionsQuery,
        handleChange,
        handleSelectUser,
        handleRemoveUser,
        handleRoleChange,
        handleUpdatePermissions,
        setShowSuggestions,
        getPermissionsPerDocument,
        isAdmin,
        isEditor,
        generalPermission,
        userInfo,
        arrayPermissionToOne,
        isLoading,
        mindyAdmin,
    } = usePermissions(file, isOpen, onClose, ownerAvatar);

    return (
        <div className={`${isVisible ? 'block' : 'hidden md:block'} w-full overflow-auto border rounded-md`}>
            <div className="p-4 border-b">
                <h3 className="font-medium">Permisos</h3>
            </div>

            {isLoading ? (
                <div className="flex-grow flex items-center justify-center p-8">
                    <div className="text-center space-y-2">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {getPermissionsPerDocument.isLoading ? 'Cargando permisos...' : 'Guardando cambios...'}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="flex-grow p-4 space-y-4">
                    {(isAdmin || isEditor || mindyAdmin) && (
                        <div className="relative" ref={suggestionsRef}>
                            <TextField
                                ref={inputRef}
                                value={searchValue}
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                name="search"
                                onChange={handleChange}
                                placeholder="añade personas"
                                onFocus={() => searchValue.trim() && setShowSuggestions(true)}
                            />

                            {showSuggestions && suggestions.length > 0 && (
                                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                    {suggestions.map((user) => (
                                        <Button
                                            key={user.id}
                                            type="button"
                                            variant="ghost"
                                            className="flex items-center gap-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer w-full text-left justify-start h-auto"
                                            onClick={() => handleSelectUser(user)}
                                            disabled={user.roles?.some((role) => isAdminRoleName(role.name))}
                                        >
                                            <Avatar className="h-6 w-6">
                                                {user.user_photo ? (
                                                    <AvatarImage
                                                        src={user.user_photo}
                                                        alt={`${user.user_name || 'Usuario'}`}
                                                    />
                                                ) : null}
                                                <AvatarFallback className="text-xs">
                                                    {user.user_name?.charAt(0) || user.user_email?.charAt(0) || '?'}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className="flex-1">
                                                <p className="text-sm font-medium">
                                                    {user.user_name || 'Usuario sin nombre'}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    {user.user_email}
                                                </p>
                                                {user.organization_id && (
                                                    <p className="text-xs text-gray-400 dark:text-gray-500">
                                                        {user.organization_id}
                                                    </p>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-400 dark:text-gray-500">
                                                {user.roles?.map(
                                                    (role) => isAdminRoleName(role.name) && 'Administrador',
                                                )}
                                            </p>
                                        </Button>
                                    ))}
                                </div>
                            )}

                            {showSuggestions && searchValue.trim() && suggestions.length === 0 && (
                                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg p-3">
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        No se encontraron resultados
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-sm font-medium">Personas con acceso</h4>
                        <TooltipFilePermission />
                    </div>

                    <div className="space-y-2">
                        {selectedUsers
                            .filter((permission) => permission.user_id)
                            .map((permission) => {
                                const isCurrentUser = permission.user_id === userInfo?.id;

                                return (
                                    <div key={permission.id} className="min-h-12 p-2 rounded flex gap-2 items-center">
                                        <Avatar className="h-8 w-8 !rounded-full overflow-hidden">
                                            <AvatarImage
                                                className="cursor-pointer !rounded-full object-cover"
                                                src={permission.user_photo ?? ''}
                                                alt={`${permission.user_name || 'Usuario'}`}
                                            />
                                            <AvatarFallback className="!rounded-full">
                                                {permission.user_name?.charAt(0) ||
                                                    permission.user_email?.charAt(0) ||
                                                    '?'}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="flex flex-col justify-start flex-1 min-w-0">
                                            <p className="font-roboto text-sm font-medium dark:text-white truncate">
                                                {permission.user_name || 'Usuario sin nombre'}
                                            </p>
                                            <p className="font-roboto text-xs font-normal text-[#ABB3BA] truncate">
                                                {permission.user_email}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {file.uploaded_by?.userId === permission.user_id ? (
                                                <span className="text-xs text-gray-400 rounded-full px-2 py-1">
                                                    Propietario
                                                </span>
                                            ) : (
                                                <>
                                                    <select
                                                        value={arrayPermissionToOne(permission.permissions) || 'viewer'}
                                                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                                            if (isAdmin || isEditor || mindyAdmin) {
                                                                const selectedRole = getAllPermissionsQuery.data?.find(
                                                                    (option: DocumentRoles) =>
                                                                        option.id === e.target.value,
                                                                );
                                                                if (selectedRole) {
                                                                    handleRoleChange(permission.user_id ?? null, {
                                                                        id: selectedRole.id,
                                                                        name: selectedRole.name,
                                                                    });
                                                                }
                                                            }
                                                        }}
                                                        className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                        disabled={
                                                            (!isAdmin && !isEditor && !mindyAdmin) || isCurrentUser
                                                        }
                                                    >
                                                        {getAllPermissionsQuery.data
                                                            ?.filter((option: DocumentRoles) => {
                                                                if (isEditor && !isAdmin) {
                                                                    return option.code !== 'admin';
                                                                }
                                                                return true;
                                                            })
                                                            ?.map((option: DocumentRoles) => (
                                                                <option key={option.id} value={option.id}>
                                                                    {option.name}
                                                                </option>
                                                            ))}
                                                    </select>
                                                    {(isAdmin || isEditor || mindyAdmin) &&
                                                        permission.user_id &&
                                                        !isCurrentUser && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveUser(permission.user_id)}
                                                                className="text-gray-400 hover:text-red-500 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                                                title="Eliminar usuario"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                    </div>

                    <hr />
                    <div className="text-sm font-medium mb-2">Acceso General</div>
                    <div>
                        <select
                            value={arrayPermissionToOne(generalPermission?.permissions ?? []) || 'none'}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                if (e.target.value === 'none') {
                                    handleRoleChange(null, { id: 'none', name: 'Sin restricción' });
                                    return;
                                }

                                const selectedRole = getAllPermissionsQuery.data?.find(
                                    (option: DocumentRoles) => option.id === e.target.value,
                                );

                                if (selectedRole) {
                                    handleRoleChange(null, selectedRole);
                                }
                            }}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={!isAdmin && !isEditor && !mindyAdmin}
                        >
                            <option value="none">Restringido</option>
                            {getAllPermissionsQuery.data
                                ?.filter((option: DocumentRoles) => option.code !== 'admin' && option.code !== 'editor')
                                .map((option: DocumentRoles) => (
                                    <option key={option.id} value={option.id}>
                                        {option.name}
                                    </option>
                                ))}
                        </select>
                    </div>
                </div>
            )}

            <div className="flex items-center justify-end gap-2 p-4 border-t">
                <Button onClick={onClose} variant="ghost" disabled={isLoading}>
                    Cancelar
                </Button>
                {(isAdmin || isEditor || mindyAdmin) && (
                    <Button onClick={() => handleUpdatePermissions()} disabled={isLoading}>
                        {isLoading ? 'Guardando...' : 'Guardar Cambios'}
                    </Button>
                )}
            </div>
        </div>
    );
}
